import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Phone, Mail, MessageCircle, Trash2, RefreshCw,
  Users, Inbox, GraduationCap, Search,
  DoorOpen, Globe, UserCog, ClipboardList, CalendarCheck, IndianRupee,
  ClipboardCheck,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import api from "@/lib/api";
import { buildWhatsAppLink, CONTACT } from "@/lib/contact";
import WalkInDialog from "@/components/site/WalkInDialog";
import StaffManager from "@/components/site/StaffManager";
import MyProfileDialog from "@/components/site/MyProfileDialog";
import AttendancePanel from "@/components/site/AttendancePanel";
import PayrollPanel from "@/components/site/PayrollPanel";
import StaffSelfPanel from "@/components/site/StaffSelfPanel";
import AdmissionsPanel from "@/components/site/AdmissionsPanel";

const STATUS_OPTIONS = ["new", "contacted", "enrolled", "closed"];
const STATUS_STYLES = {
  new: "bg-sky-100 text-ark-skyDeep",
  contacted: "bg-amber-100 text-amber-700",
  enrolled: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-600",
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, enrolled: 0, walkins: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const email = localStorage.getItem("la_admin_email") || "Admin";
  const [name, setName] = useState(localStorage.getItem("la_admin_name") || email);
  const role = localStorage.getItem("la_admin_role") || "staff";
  const isSuper = role === "superadmin";
  // Coordinator = old "staff" role. Sees inquiries + admissions + own me tab.
  const isCoordinator = role === "staff";
  // Self-service roles: teachers, caretakers, security, van drivers.
  // They see ONLY the "Me" tab (own attendance + own payslips + own profile).
  const isSelfService = !isSuper && !isCoordinator;
  const [tab, setTab] = useState(isSelfService ? "me" : "inquiries");

  const fetchData = async () => {
    if (isSelfService) { setLoading(false); return; }
    setLoading(true);
    try {
      const [inq, st] = await Promise.all([
        api.get("/admin/inquiries"),
        api.get("/admin/stats"),
      ]);
      setInquiries(inq.data);
      setStats(st.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("la_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load inquiries.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("la_admin_token")) {
      navigate("/admin/login");
      return;
    }
    fetchData();
    // eslint-disable-next-line
  }, []);

  const logout = () => {
    localStorage.removeItem("la_admin_token");
    localStorage.removeItem("la_admin_email");
    localStorage.removeItem("la_admin_role");
    localStorage.removeItem("la_admin_name");
    navigate("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      setInquiries((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated.");
      const st = await api.get("/admin/stats");
      setStats(st.data);
    } catch {
      toast.error("Could not update.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/inquiries/${id}`);
      setInquiries((arr) => arr.filter((x) => x.id !== id));
      toast.success("Inquiry removed.");
      const st = await api.get("/admin/stats");
      setStats(st.data);
    } catch {
      toast.error("Could not delete.");
    }
  };

  const filtered = inquiries.filter((q) => {
    const matchSearch =
      !search ||
      q.parent_name?.toLowerCase().includes(search.toLowerCase()) ||
      q.phone?.includes(search) ||
      q.email?.toLowerCase().includes(search.toLowerCase()) ||
      q.child_name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || q.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const statCards = [
    { label: "Total Inquiries", value: stats.total, icon: Inbox, color: "bg-sky-100 text-ark-skyDeep" },
    { label: "New", value: stats.new, icon: Users, color: "bg-amber-100 text-amber-600" },
    { label: "Enrolled", value: stats.enrolled, icon: GraduationCap, color: "bg-emerald-100 text-emerald-600" },
    { label: "Walk-ins", value: stats.walkins || 0, icon: DoorOpen, color: "bg-violet-100 text-violet-600" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={CONTACT.logoUrl} alt="Little Ark" className="h-11 w-auto object-contain" />
            <div className="hidden sm:block text-[11px] uppercase tracking-[0.18em] text-ark-skyDeep font-bold">
              Admin Dashboard
            </div>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex flex-col items-end leading-tight">
              <span data-testid="header-user-name" className="text-sm font-semibold text-slate-700 max-w-[90px] sm:max-w-[180px] truncate">{name}</span>
              <Badge
                data-testid="role-badge"
                className={`rounded-full border-0 text-[10px] font-bold ${isSuper ? "bg-ark-ink text-white" : isCoordinator ? "bg-sky-100 text-ark-skyDeep" : "bg-teal-100 text-ark-tealDeep"}`}
              >
                {(isSuper ? "SUPER ADMIN" : isCoordinator ? "COORDINATOR" : role.replace("_", " ")).toUpperCase()}
              </Badge>
            </div>
            {!isSelfService && <WalkInDialog onCreated={fetchData} />}
            <MyProfileDialog onUpdated={(d) => setName(d.name || d.email)} />
            {!isSelfService && (
              <Button
                variant="ghost"
                onClick={fetchData}
                data-testid="refresh-btn"
                className="rounded-full"
                size="sm"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
            <Button
              onClick={logout}
              data-testid="admin-logout-btn"
              variant="outline"
              size="sm"
              className="rounded-full"
            >
              <LogOut className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 lg:px-8 py-8">
        <h1 className="font-display font-bold text-2xl md:text-3xl text-ark-ink">
          {isSuper ? "Admin Dashboard" : isCoordinator ? "Coordinator Dashboard" : "My Dashboard"}
        </h1>
        <p className="text-slate-500 mt-1">
          {isSuper
            ? "Manage inquiries, walk-ins and staff accounts."
            : isCoordinator
              ? "View inquiries and record new walk-in inquiries."
              : "Mark your attendance and view your payslips."}
        </p>

        {!isSelfService && (
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {statCards.map((s) => (
              <Card key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`} className="border-0 rounded-3xl shadow-ark-soft">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className={`h-12 w-12 rounded-2xl ${s.color} flex items-center justify-center`}>
                    <s.icon className="h-6 w-6" strokeWidth={2.4} />
                  </div>
                  <div>
                    <div className="text-2xl font-display font-bold text-ark-ink">{s.value}</div>
                    <div className="text-xs text-slate-500 font-semibold">{s.label}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="rounded-full bg-slate-100 p-1 flex-wrap h-auto" data-testid="dashboard-tabs">
            {!isSelfService && (
              <TabsTrigger value="inquiries" data-testid="tab-inquiries" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-ark-skyDeep font-display font-semibold">
                <ClipboardList className="h-4 w-4 mr-2" /> Inquiries
              </TabsTrigger>
            )}
            {!isSelfService && (
              <TabsTrigger value="admissions" data-testid="tab-admissions" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-ark-skyDeep font-display font-semibold">
                <ClipboardCheck className="h-4 w-4 mr-2" /> Admissions
              </TabsTrigger>
            )}
            {isSuper && (
              <TabsTrigger value="staff" data-testid="tab-staff" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-ark-skyDeep font-display font-semibold">
                <UserCog className="h-4 w-4 mr-2" /> Staff
              </TabsTrigger>
            )}
            {isSuper && (
              <TabsTrigger value="attendance" data-testid="tab-attendance" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-ark-skyDeep font-display font-semibold">
                <CalendarCheck className="h-4 w-4 mr-2" /> Attendance
              </TabsTrigger>
            )}
            {isSuper && (
              <TabsTrigger value="payroll" data-testid="tab-payroll" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-ark-skyDeep font-display font-semibold">
                <IndianRupee className="h-4 w-4 mr-2" /> Payroll
              </TabsTrigger>
            )}
            {!isSuper && (
              <TabsTrigger value="me" data-testid="tab-me" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-ark-skyDeep font-display font-semibold">
                <CalendarCheck className="h-4 w-4 mr-2" /> {isSelfService ? "My Attendance & Payslips" : "My Attendance & Pay"}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="inquiries" className="mt-6">
        <Card className="border-0 rounded-3xl shadow-ark-soft">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="search-input"
                  placeholder="Search parent, phone, child…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-11 h-11 rounded-2xl border-slate-200"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-48 h-11 rounded-2xl border-slate-200" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mt-6 overflow-x-auto" data-testid="admin-dashboard-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Parent</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Child</TableHead>
                    <TableHead>Program</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-slate-500">Loading…</TableCell>
                    </TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                        No inquiries yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((q) => (
                      <TableRow key={q.id} data-testid={`inquiry-row-${q.id}`}>
                        <TableCell>
                          <div className="font-semibold text-ark-ink">{q.parent_name}</div>
                          {q.message && (
                            <div className="text-xs text-slate-500 mt-1 max-w-xs truncate" title={q.message}>
                              {q.message}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm space-y-1">
                            <a href={`tel:${q.phone}`} className="flex items-center gap-1.5 text-slate-700 hover:text-ark-sky">
                              <Phone className="h-3.5 w-3.5" /> {q.phone}
                            </a>
                            {q.email && (
                              <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 text-slate-500 hover:text-ark-sky">
                                <Mail className="h-3.5 w-3.5" /> {q.email}
                              </a>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-slate-700">{q.child_name || "—"}</div>
                          {q.child_age && <div className="text-xs text-slate-500">{q.child_age}</div>}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="rounded-full font-semibold">
                            {q.program || "—"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {q.source === "walkin" ? (
                            <div data-testid={`source-${q.id}`}>
                              <Badge className="bg-violet-100 text-violet-700 border-0 rounded-full font-semibold">
                                <DoorOpen className="h-3 w-3 mr-1" /> Walk-in
                              </Badge>
                              {q.added_by && (
                                <div className="text-[11px] text-slate-400 mt-1">by {q.added_by}</div>
                              )}
                            </div>
                          ) : (
                            <Badge data-testid={`source-${q.id}`} className="bg-sky-100 text-ark-skyDeep border-0 rounded-full font-semibold">
                              <Globe className="h-3 w-3 mr-1" /> Website
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {isSuper ? (
                            <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                              <SelectTrigger
                                data-testid={`status-select-${q.id}`}
                                className={`w-32 h-8 text-xs font-semibold rounded-full border-0 ${STATUS_STYLES[q.status] || ""}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUS_OPTIONS.map((s) => (
                                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge
                              data-testid={`status-badge-${q.id}`}
                              className={`capitalize rounded-full border-0 font-semibold ${STATUS_STYLES[q.status] || ""}`}
                            >
                              {q.status}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(q.created_at).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <a
                              href={buildWhatsAppLink(
                                `Hi ${q.parent_name}, thanks for your interest in Little Ark Preschool!`
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`whatsapp-${q.id}`}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-green-50 text-green-600"
                              title="WhatsApp"
                            >
                              <MessageCircle className="h-4 w-4" />
                            </a>
                            {isSuper && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button
                                    data-testid={`delete-${q.id}`}
                                    className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-red-50 text-red-500"
                                    title="Delete"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete this inquiry?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This action cannot be undone. The inquiry from {q.parent_name} will be permanently removed.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => remove(q.id)}
                                      className="bg-red-500 hover:bg-red-600"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          {isSuper && (
            <TabsContent value="staff" className="mt-6" data-testid="staff-tab-content">
              <StaffManager />
            </TabsContent>
          )}
          <TabsContent value="admissions" className="mt-6" data-testid="admissions-tab-content">
            <AdmissionsPanel isSuper={isSuper} />
          </TabsContent>
          {isSuper && (
            <TabsContent value="attendance" className="mt-6" data-testid="attendance-tab-content">
              <AttendancePanel />
            </TabsContent>
          )}
          {isSuper && (
            <TabsContent value="payroll" className="mt-6" data-testid="payroll-tab-content">
              <PayrollPanel />
            </TabsContent>
          )}
          {!isSuper && (
            <TabsContent value="me" className="mt-6" data-testid="me-tab-content">
              <StaffSelfPanel />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
